'use client';

import { Locale } from "@/constants/locales";
import { SchemaFields, FAQItem } from "@/types";
import { SchemaGenerator } from "@/lib/schema";
import Script from "next/script";
import { useEffect } from "react";

interface SchemaRouteData {
    href: Record<Locale, string>;
    isPublished: boolean;
    schema?: {
        fields: string[];
        data?: any;
    };
    meta: {
        title: Record<Locale, string>;
        description: Partial<Record<Locale, string>>;
    };
    postData?: any;
}

export default function SchemaScript({
    route,
    locale,
    faqItems
}: {
    route: SchemaRouteData,
    locale: Locale,
    faqItems?: FAQItem[]
}) {
    // Create a unique key based on route and locale
    const schemaKey = route?.href?.[locale] ? `schema-${route.href[locale]}` : '';

    useEffect(() => {
        // Cleanup function to remove old schema scripts
        return () => {
            const oldScripts = document.querySelectorAll('script[type="application/ld+json"]');
            oldScripts.forEach(script => {
                if (script.id !== schemaKey) {
                    script.remove();
                }
            });
        };
    }, [schemaKey]);

    if (!route || !route.isPublished) return null;
    if (!route.schema || !route.schema.fields.length) return null;

    const schemaGenerator = new SchemaGenerator(route as any, locale);

    const schemas = (route.schema as any).fields.map((field: string) => {
        if (field === SchemaFields.generateFAQSchema) {
            return schemaGenerator.generateFAQSchema(faqItems || []);
        }

        return schemaGenerator[field]();
    });

    return (
        <Script
            id={schemaKey}
            key={schemaKey}
            type="application/ld+json"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(schemas) }}
        />
    );
}